import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ChocolateType {
  name: string;
  cocoa: string;
  flavor: string;
  color: string;
}

interface ProductionStep {
  label: string;
  icon: string;
}

interface FlavorCompound {
  taste: string;
  compound: string;
  icon: string;
  color: string;
}

interface HealthBenefit {
  title: string;
  icon: string;
}

interface CocoaProducer {
  name: string;
  x: number;
  y: number;
}

interface ChocolateTemplateProps {
  topic: string;
  subtitle: string;
  chocolateTypes: ChocolateType[];
  productionSteps: ProductionStep[];
  flavorCompounds: FlavorCompound[];
  healthBenefits: HealthBenefit[];
  producers: CocoaProducer[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: ChocolateTemplateProps = {
  topic: '巧克力学与可可科学',
  subtitle: 'Chocolate Science & Cocoa',
  chocolateTypes: [
    { name: '黑巧克力70%+', cocoa: '70-99%', flavor: '浓郁苦香，回甘持久', color: '#3c1a08' },
    { name: '牛奶巧克力', cocoa: '25-40%', flavor: '丝滑柔和，奶香馥郁', color: '#d4a574' },
    { name: '白巧克力', cocoa: '可可脂', flavor: '甜蜜香滑，奶油质感', color: '#fef3c7' },
    { name: '红宝石巧克力', cocoa: '~47.3%', flavor: '天然粉色，浆果酸甜', color: '#ec4899' },
  ],
  productionSteps: [
    { label: '采摘', icon: '🌿' },
    { label: '发酵', icon: '🫘' },
    { label: '干燥', icon: '☀️' },
    { label: '烘焙', icon: '🔥' },
    { label: '研磨', icon: '⚙️' },
    { label: '精炼', icon: '✨' },
  ],
  flavorCompounds: [
    { taste: '苦', compound: '可可碱 Theobromine', icon: '💧', color: '#7c3aed' },
    { taste: '酸', compound: '柠檬酸 / 醋酸', icon: '🍋', color: '#f59e0b' },
    { taste: '甜', compound: '蔗糖 Sucrose', icon: '🍬', color: '#ec4899' },
    { taste: '香', compound: '吡嗪类 Pyrazines', icon: '🌸', color: '#a78bfa' },
  ],
  healthBenefits: [
    { title: '抗氧化', icon: '🛡️' },
    { title: '改善情绪', icon: '😊' },
    { title: '降血压', icon: '❤️' },
    { title: '提升认知', icon: '🧠' },
  ],
  producers: [
    { name: '科特迪瓦', x: 0.47, y: 0.48 },
    { name: '加纳', x: 0.49, y: 0.58 },
    { name: '厄瓜多尔', x: 0.24, y: 0.52 },
  ],
  stats: [
    { value: '500万吨/年', label: '全球年消费量' },
    { value: '4000年', label: '可可树栽培史' },
    { value: '600+', label: '风味化合物' },
  ],
  bottomQuote:
    '从热带雨林的可可果到入口即化的丝滑——每一颗巧克力都是科学与艺术的结晶',
};

export const ChocolateTemplate: React.FC<Partial<ChocolateTemplateProps>> = (
  props,
) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const beanDraw = interpolate(frame, [20, 90], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const beanFill = interpolate(frame, [70, 110], [0, 0.45], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const chainStart = 80;
  const chocoStart = 200;
  const temperStart = 280;
  const flavorStart = 360;
  const healthStart = 420;
  const mapStart = 480;
  const statsStart = 540;
  const quoteStart = 580;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const temperDraw = interpolate(frame, [temperStart, temperStart + 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a0a06 0%, #0f0604 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Cocoa particle dots */}
      {Array.from({ length: 45 }).map((_, i) => {
        const x = (i * 263) % 1920;
        const y = (i * 149) % 1080;
        const size = 2 + (i % 4);
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#3c1a08',
              opacity: 0.12,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#3c1a08',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#d4a574',
            flexShrink: 0,
          }}
        >
          🍫 巧克力科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fef3c7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#a8a29e',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Bean SVG ===== */}
      <div style={{ position: 'absolute', top: 130, left: 36, width: 740 }}>
        {/* Cocoa Bean Cross-Section */}
        <svg width="720" height="470" viewBox="0 0 520 340">
          <defs>
            <linearGradient id="chocBeanGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#3c1a08" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#d4a574" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="chocNibGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3c1a08" stopOpacity="0.5" />
            </linearGradient>
          </defs>

          <ellipse
            cx="200"
            cy="170"
            rx="150"
            ry="110"
            fill="none"
            stroke="#d4a574"
            strokeWidth="3"
            strokeDasharray="900"
            strokeDashoffset={900 * (1 - beanDraw)}
            opacity="0.9"
          />
          <ellipse
            cx="200"
            cy="170"
            rx="148"
            ry="108"
            fill="url(#chocBeanGrad)"
            opacity={beanFill}
          />
          <line
            x1="200"
            y1="62"
            x2="200"
            y2="278"
            stroke="#fef3c7"
            strokeWidth="1.5"
            strokeDasharray="6 4"
            opacity={interpolate(frame, [50, 80], [0, 0.6], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <path
            d="M120,130 Q160,170 120,210"
            fill="none"
            stroke="#d4a574"
            strokeWidth="1.5"
            opacity={interpolate(frame, [60, 90], [0, 0.7], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <path
            d="M280,130 Q240,170 280,210"
            fill="none"
            stroke="#d4a574"
            strokeWidth="1.5"
            opacity={interpolate(frame, [65, 95], [0, 0.7], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <ellipse
            cx="200"
            cy="170"
            rx="100"
            ry="70"
            fill="none"
            stroke="#ec4899"
            strokeWidth="1"
            strokeDasharray="4 6"
            opacity={interpolate(frame, [70, 100], [0, 0.35], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <ellipse
            cx="200"
            cy="170"
            rx="60"
            ry="42"
            fill="url(#chocNibGrad)"
            opacity={interpolate(frame, [80, 110], [0, 0.5], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          <line
            x1="348"
            y1="95"
            x2="320"
            y2="110"
            stroke="#d4a574"
            strokeWidth="0.8"
            opacity={interpolate(frame, [85, 105], [0, 0.5], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <text
            x="356"
            y="100"
            fill="#d4a574"
            fontSize="22"
            opacity={interpolate(frame, [85, 105], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            外壳 Testa
          </text>

          <line
            x1="348"
            y1="165"
            x2="260"
            y2="170"
            stroke="#fef3c7"
            strokeWidth="0.8"
            opacity={interpolate(frame, [90, 110], [0, 0.5], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <text
            x="356"
            y="170"
            fill="#fef3c7"
            fontSize="22"
            opacity={interpolate(frame, [90, 110], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            可可豆仁 Nib
          </text>

          <line
            x1="348"
            y1="235"
            x2="280"
            y2="210"
            stroke="#ec4899"
            strokeWidth="0.8"
            opacity={interpolate(frame, [95, 115], [0, 0.5], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <text
            x="356"
            y="240"
            fill="#ec4899"
            fontSize="22"
            opacity={interpolate(frame, [95, 115], [0, 0.8], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            发酵层
          </text>
        </svg>

      </div>

      {/* ===== CENTER COLUMN: Chocolate Types + Tempering Curve ===== */}
      <div style={{ position: 'absolute', top: 105, left: 800, width: 1000 }}>
        {/* Chocolate Types */}
        <div>
          <div
            style={{
              fontSize: 24,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            巧克力分类
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {p.chocolateTypes.map((ct, i) => {
              const s = spring({
                frame: frame - (chocoStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const labelColor =
                ct.color === '#fef3c7' ? '#fbbf24' : ct.color;
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: '#1c1917',
                    borderRadius: 12,
                    padding: '12px 10px',
                    border: `1.5px solid ${ct.color}50`,
                    opacity: s,
                    transform: `translateY(${(1 - s) * 20}px)`,
                    marginRight: 5,
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 40,
                      borderRadius: 6,
                      background: ct.color,
                      marginBottom: 6,
                      boxShadow: `0 0 12px ${ct.color}30`,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: labelColor,
                      marginBottom: 2,
                    }}
                  >
                    {ct.name}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      color: '#fef3c7',
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                    可可 {ct.cocoa}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      color: '#a8a29e',
                      lineHeight: 1.4,
                    }}
                  >
                    {ct.flavor}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tempering Curve */}
        <div style={{ marginTop: 57 }}>
          <div
            style={{
              fontSize: 24,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            调温曲线 · 晶体控制
          </div>
          <svg width="980" height="200" viewBox="0 0 1008 210">
            {[30, 70, 110, 150].map((y) => (
              <line
                key={y}
                x1="50"
                y1={y}
                x2="1008"
                y2={y}
                stroke="#3c1a08"
                strokeWidth="1.5"
                opacity="0.2"
              />
            ))}
            <text x="0" y="38" fill="#a8a29e" fontSize="28">
              50°C
            </text>
            <text x="0" y="78" fill="#a8a29e" fontSize="28">
              40°C
            </text>
            <text x="0" y="118" fill="#a8a29e" fontSize="28">
              30°C
            </text>
            <text x="0" y="158" fill="#a8a29e" fontSize="28">
              20°C
            </text>

            <path
              d="M60,130 Q200,130 320,30 Q420,30 540,118 Q600,118 660,98 L880,98"
              fill="none"
              stroke="#d4a574"
              strokeWidth="4"
              strokeDasharray="1200"
              strokeDashoffset={1200 * (1 - temperDraw)}
              strokeLinecap="round"
            />

            <rect
              x="490"
              y="100"
              width="150"
              height="36"
              rx="6"
              fill="#ec489920"
              stroke="#ec489950"
              strokeWidth="1.5"
              opacity={interpolate(
                frame,
                [temperStart + 40, temperStart + 60],
                [0, 0.8],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
            <text
              x="565"
              y="124"
              fill="#ec4899"
              fontSize="28"
              textAnchor="middle"
              opacity={interpolate(
                frame,
                [temperStart + 45, temperStart + 65],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              β(V) 晶体形成区
            </text>

            <text
              x="260"
              y="28"
              fill="#fef3c7"
              fontSize="28"
              textAnchor="middle"
              opacity={interpolate(
                frame,
                [temperStart + 15, temperStart + 35],
                [0, 0.9],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              加热融化
            </text>
            <text
              x="490"
              y="155"
              fill="#fef3c7"
              fontSize="28"
              textAnchor="middle"
              opacity={interpolate(
                frame,
                [temperStart + 35, temperStart + 55],
                [0, 0.9],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              冷却结晶
            </text>
            <text
              x="770"
              y="85"
              fill="#fef3c7"
              fontSize="28"
              textAnchor="middle"
              opacity={interpolate(
                frame,
                [temperStart + 55, temperStart + 75],
                [0, 0.9],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              再加热 · 工作温度
            </text>
          </svg>
        </div>
      </div>

      {/* ===== Production Chain + Cocoa Belt Map (same row) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 660,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 40,
        }}
      >
        {/* Production Chain (left half) */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            生产链
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {p.productionSteps.map((step, i) => {
              const s = spring({
                frame: frame - (chainStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const isLast = i === p.productionSteps.length - 1;
              return (
                <React.Fragment key={i}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: 110,
                      opacity: s,
                      transform: `translateY(${(1 - s) * 15}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: '#1c1917',
                        border: '2px solid #3c1a08',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 50,
                        flexShrink: 0,
                      }}
                    >
                      {step.icon}
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: '#d4a574',
                        marginTop: 4,
                      }}
                    >
                      {step.label}
                    </div>
                  </div>
                  {!isLast && (
                    <div
                      style={{
                        width: 24,
                        height: 2,
                        background: '#3c1a08',
                        marginTop: 50,
                        flexShrink: 0,
                        opacity: interpolate(
                          frame,
                          [chainStart + i * 18 + 10, chainStart + i * 18 + 22],
                          [0, 0.6],
                          {
                            extrapolateLeft: 'clamp',
                            extrapolateRight: 'clamp',
                          },
                        ),
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Cocoa Belt Map (right half) */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            可可带 · 热带种植区
          </div>
          <svg width="100%" height="180" viewBox="0 0 1008 160">
            <path
              d="M100,25 Q130,15 155,30 L170,55 Q178,78 160,95 L145,108 Q132,115 118,105 L100,82 Q88,55 100,25 Z"
              fill="none"
              stroke="#d4a574"
              strokeWidth="4"
              opacity={interpolate(frame, [mapStart, mapStart + 30], [0, 0.6], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })}
            />
            <path
              d="M430,18 Q458,10 478,28 L490,60 Q494,82 482,102 L466,122 Q455,132 440,120 L425,90 Q418,55 430,18 Z"
              fill="none"
              stroke="#d4a574"
              strokeWidth="4"
              opacity={interpolate(
                frame,
                [mapStart + 10, mapStart + 40],
                [0, 0.6],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
            <path
              d="M710,35 Q740,25 765,38 L778,60 Q782,78 768,90 L748,96 Q728,90 718,72 L710,35 Z"
              fill="none"
              stroke="#d4a574"
              strokeWidth="4"
              opacity={interpolate(
                frame,
                [mapStart + 20, mapStart + 50],
                [0, 0.6],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />

            <rect
              x="0"
              y="58"
              width="1008"
              height="34"
              fill="#3c1a08"
              opacity={interpolate(
                frame,
                [mapStart + 10, mapStart + 35],
                [0, 0.2],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
            <line
              x1="0"
              y1="75"
              x2="1008"
              y2="75"
              stroke="#d4a574"
              strokeWidth="3"
              strokeDasharray="12 6"
              opacity={interpolate(
                frame,
                [mapStart + 15, mapStart + 40],
                [0, 0.5],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
            <text
              x="920"
              y="50"
              fill="#d4a574"
              fontSize="14"
              opacity={interpolate(
                frame,
                [mapStart + 20, mapStart + 45],
                [0, 0.8],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              赤道 ±20°
            </text>

            {p.producers.map((pr, i) => {
              const px = pr.x * 1008;
              const py = pr.y * 160;
              const labelOffsets = [
                { dx: 18, dy: -14 },
                { dx: 18, dy: 30 },
                { dx: 18, dy: 5 },
              ];
              const offset = labelOffsets[i] || { dx: 18, dy: 5 };
              const markerS = spring({
                frame: frame - (mapStart + 25 + i * 15),
                fps,
                config: { damping: 10, stiffness: 150 },
              });
              return (
                <g key={i} opacity={markerS}>
                  <circle cx={px} cy={py} r={12} fill="#ec4899" opacity="0.3" />
                  <circle cx={px} cy={py} r={6} fill="#ec4899" />
                  <text
                    x={px + offset.dx}
                    y={py + offset.dy}
                    fill="#fef3c7"
                    fontSize="16"
                    fontWeight="700"
                  >
                    {pr.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* ===== Flavor Compounds + Health Benefits (same row) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 880,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 40,
        }}
      >
        {/* Flavor Compounds (left half) */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              color: '#a8a29e',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            风味化合物
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {p.flavorCompounds.map((fc, i) => {
              const s = spring({
                frame: frame - (flavorStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: '#1c1917',
                    borderRadius: 14,
                    padding: '18px 10px',
                    textAlign: 'center',
                    border: '1px solid #3c1a0830',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 30 }}>{fc.icon}</span>
                    <span
                      style={{ fontSize: 26, fontWeight: 900, color: fc.color }}
                    >
                      {fc.taste}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      color: '#a8a29e',
                      marginTop: 5,
                      lineHeight: 1.4,
                    }}
                  >
                    {fc.compound}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Health Benefits (right half) */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              color: '#9e4211',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            黑巧克力健康益处
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {p.healthBenefits.map((hb, i) => {
              const s = spring({
                frame: frame - (healthStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: '#1c1917',
                    borderRadius: 14,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #3c1a0830',
                    opacity: s,
                    transform: `translateY(${(1 - s) * 16}px)`,
                  }}
                >
                  <div style={{ fontSize: 30, marginBottom: 4 }}>{hb.icon}</div>
                  <div
                    style={{ fontSize: 24, fontWeight: 900, color: '#d4a574' }}
                  >
                    {hb.title}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1c1917',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #44403c',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 34, fontWeight: 900, color: '#d4a574' }}
              >
                {st.value}
              </div>
              <div
                style={{ fontSize: 16, color: '#a8a29e', marginTop: 4 }}
              >
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1c1917',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #3c1a08',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🍫</span>
          <span
            style={{ fontSize: 17, color: '#fef3c7', lineHeight: 1.6 }}
          >
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 600], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #3c1a08, #d4a574, #ec4899)',
        }}
      />
    </AbsoluteFill>
  );
};
